import React from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";

import NewVersionPackageDetails from "components/NewVersionPackageDetails/NewVersionPackageDetails";
import {
  useCreatePackageVersion,
  usePackageVersion,
} from "store/backend/apiHooks";
import AddPackageFeatureOffering from "components/AddPackageFeatureOffering/AddPackageFeatureOffering";
import CreatedPackageFeatureOfferings from "components/CreatedPackageFeatureOfferings/CreatedPackageFeatureOfferings";
import { DEFAULT_PRODUCT, DEFAULT_SERVICE } from "constants/config";
import { PrimaryButton } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { CURRENCY_ID, TENANT_ID } from "constants/constants";

import styles from "./CreateNewPackageVersion.module.scss";

const getInitialValues = (packageDetails) => ({
  package_group_id: packageDetails.package_group_id,
  name: packageDetails?.name,
  display_name: packageDetails?.display_name || "",
  description: packageDetails?.description || "",
  start_selling_date: packageDetails?.start_selling_date || "",
  end_selling_date: packageDetails?.end_selling_date || "",
  listed_price: packageDetails?.price.listed_price || 0,
  selling_price: packageDetails?.price.selling_price || 0,
  is_percentage: packageDetails?.price.is_percentage || false,
  discount_value: packageDetails?.price.discount_value || 0,
  feature_offerings: packageDetails?.feature_offerings || {
    products: [],
    services: [],
  },
  newProductFeatureOffering: [DEFAULT_PRODUCT],
  newServiceFeatureOffering: [DEFAULT_SERVICE],
});

const productOfferingSchema = Yup.object({
  productOffering: Yup.object()
    .shape({
      value: Yup.string().required("Product offering is required"),
    })
    .test(
      "is-not-empty",
      "Product offering is required",
      (obj) => obj && obj.value && obj.value.trim() !== ""
    ),
  description: Yup.string()
    .required("Description is required")
    .test(
      "len",
      "Description must be limited to 500 characters",
      (val) => val.length <= 500
    ),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(1, "Quantity must be greater than 0"),
});

const serviceOfferingSchema = Yup.object({
  feature: Yup.object()
    .shape({
      value: Yup.string().required("Feature is required"),
    })
    .test(
      "is-not-empty",
      "Feature is required",
      (obj) => obj && obj.value && obj.value.trim() !== ""
    ),
  offering: Yup.object()
    .shape({
      value: Yup.string().required("Offering is required"),
    })
    .test(
      "is-not-empty",
      "Offering is required",
      (obj) => obj && obj.value && obj.value.trim() !== ""
    ),
  description: Yup.string()
    .required("Description is required")
    .test(
      "len",
      "Description must be limited to 500 characters",
      (val) => val.length <= 500
    ),
    quantity: Yup.number()
  .test("conditional-validation", "Quantity is required", function (value) {
    const { duration } = this.parent;
      return duration || value;
  })
    .min(1, "Quantity must be greater than 0"),
  duration: Yup.number()
    .test("conditional-validation", "Duration is required", function (value) {
      const { quantity } = this.parent;
      return quantity || value;
    })
    .min(1, "Duration must be greater than 0"),
  durationType: Yup.object()
    .shape({
      value: Yup.string(),
    })
    .test(
      "conditional-validation",
      "Duration type is required",
      function (obj) {
        const { quantity } = this.parent;
        return quantity || (obj && obj.value && obj.value.trim() !== "");
      }
    ),
  usageDate: Yup.object()
    .shape({
      value: Yup.string().required("Usage date is required"),
    })
    .test(
      "is-not-empty",
      "Usage date is required",
      (obj) => obj && obj.value && obj.value.trim() !== ""
    ),
});

const validationSchema = Yup.object({
  display_name: Yup.string().required("Display name is required"),
  description: Yup.string()
    .required("Description is required")
    .test(
      "len",
      "Description must be limited to 500 characters",
      (val) => val.length <= 500
    ),
  start_selling_date: Yup.string().required("Start selling date is required"),
  end_selling_date: Yup.string().required("End selling date is required"),
  listed_price: Yup.number()
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  selling_price: Yup.number()
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  discount_value: Yup.string()
    .test(
      "is-valid-discount",
      "Discount must be a positive number or percentage",
      (value) => {
        if (!value) return false;

        if (value.includes("%")) {
          const percentage = parseFloat(value.replace("%", "").trim());
          return percentage > 0;
        }
        const number = parseFloat(value);
        return number > 0;
      }
    )
    .required("Discount is required"),
  newProductFeatureOffering: Yup.array().of(productOfferingSchema),
  newServiceFeatureOffering: Yup.array().of(serviceOfferingSchema),
});
const handleAddFeatureOffering = (
  setFieldValue,
  feature_offerings,
  newProductFeatureOffering = [],
  newServiceFeatureOffering = []
) => {

  const normalizeProductFeature = (product) => {
    if (product.productOffering && !isEmpty(product.productOffering)) {
      const offeringId = Number(product.productOffering.id);
      if (!offeringId) {
        return null;
      }
      return {
        offering_id: offeringId,
        offering_name: product.productOffering.label,
        offering_description: product.description,
        quantity: Number(product.quantity),
      };
    }
    return null;
  };

  const normalizeServiceFeature = (service) => {
    if (service.feature && service.offering) {
      const featureId = Number(service.feature.id);
      const offeringId = Number(service.offering.id);
      if (!featureId || !offeringId) {
        return null;
      }
      return {
        feature: {
          id: featureId,
          name: service.feature.label,
        },
        offering: {
          id: offeringId,
          name: service.offering.label,
        },
        description: service.description,
        quantity: Number(service.quantity),
        duration: Number(service.duration),
        duration_type:service.durationType,
        start_and_end_usage_date: service.usageDate,
      };
    }
    return null; 
  };

  const normalizedProducts = Array.isArray(newProductFeatureOffering)
    ? newProductFeatureOffering.map(normalizeProductFeature).filter((product) => product !== null)
    : [];

  const normalizedServices = Array.isArray(newServiceFeatureOffering)
    ? newServiceFeatureOffering.map(normalizeServiceFeature).filter((service) => service !== null)
    : [];

  setFieldValue("feature_offerings", {
    products: [...feature_offerings.products, ...normalizedProducts],
    services: [...feature_offerings.services, ...normalizedServices],
  });

  setFieldValue("newProductFeatureOffering", [DEFAULT_PRODUCT]);
  setFieldValue("newServiceFeatureOffering", [DEFAULT_SERVICE]);
};

const buildPayload = (values) => {

  const payload = {
    description: values.description,
    display_name: values.display_name,
    start_selling_date: values.start_selling_date,
    end_selling_date: values.end_selling_date,
    listed_price: values.listed_price,
    selling_price: values.selling_price,
    discount_value: values.discount_value,
    is_percentage: values.is_percentage,
    currency_id: CURRENCY_ID,
    tenant_id:TENANT_ID ,
    feature_offerings: {
      products: values.feature_offerings.products.map((product) => ({
        offering_id: product.offering_id,
        description: product.offering_description,
        quantity: product.quantity,
      })),
      services: values.feature_offerings.services.map((service) => ({
        feature_id: service.feature?.id,
        offering_id: service.offering?.id,
        description: service.description,
        quantity: service.quantity,
        duration: service.duration || null,
        duration_type_id: service.duration_type?.id || null,
        usage_date_id: service.start_and_end_usage_date?.id,
      })),
    },
  };

  return payload;
};

const handleOnClickCreatePackageVersion = (
  values,
  createPackageVersion,
  navigate,
  openSnackbar,
  packageGroupId
) => {
  const packageVersionPayload = buildPayload(values);

  createPackageVersion(
    { packageGroupId, payload: packageVersionPayload },
    {
      onSuccess: () => {
        navigate("/homePage");
        openSnackbar("Successfully created package version!", "success");
      },
      onError: (response) => {
        const errorMessages = response.message;
        openSnackbar(
          errorMessages || "Error creating package version",
          "error"
        );
      },
    }
  );
};

const handleDeleteProductFeatureOffering = (
  itemPosition,
  setFieldValue,
  values
) => {
  setFieldValue("feature_offerings", {
    products: values.feature_offerings.products.filter(
      (_, pos) => pos !== itemPosition
    ),
    services: values.feature_offerings.services,
  });
};

const handleDeleteServiceFeatureOffering = (
  featureOfferingPosition,
  setFieldValue,
  values
) => {
  setFieldValue("feature_offerings", {
    products: values.feature_offerings.products,
    services: values.feature_offerings.services.filter(
      (_, pos) => pos !== featureOfferingPosition
    ),
  });
};

const CreateNewPackageVersion = (props) => {
  const { packageGroupId, packageId } = props;
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const {
    data: packageDetails,
    isLoading: isPackageDetailsLoading,
    isError: isErrorPackageDetails,
    error: packageDetailsError,
  } = usePackageVersion(packageId);

  const {
    mutate: createPackageVersion,
    isPending: isCreatePackageVersionLoading,
  } = useCreatePackageVersion();

  if (isPackageDetailsLoading) {
    return <div>Loading...</div>;
  }
  if (isErrorPackageDetails) {
    return <div>{packageDetailsError.message}</div>;
  }

  return (
    <Formik
      initialValues={getInitialValues(packageDetails)}
      validationSchema={validationSchema}
    >
      {(formikProps) => {
        const { values, errors, touched, setFieldValue } = formikProps;

        const {
          name,
          display_name,
          description,
          start_selling_date,
          end_selling_date,
          selling_price,
          listed_price,
          discount_value,
          is_percentage,
          feature_offerings,
          newProductFeatureOffering,
          newServiceFeatureOffering,
        } = values;

        return (
          <Form className={styles.formContainer}>
            <div className={styles.createPackageVersionButton}>
              <PrimaryButton
                disabled={isCreatePackageVersionLoading}
                onClick={() =>
                  handleOnClickCreatePackageVersion(
                    values,
                    createPackageVersion,
                    navigate,
                    openSnackbar,
                    packageGroupId
                  )
                }
              >
                Create Package Version
              </PrimaryButton>
            </div>
            <NewVersionPackageDetails
              name={name}
              display_name={display_name}
              description={description}
              start_selling_date={start_selling_date}
              end_selling_date={end_selling_date}
              listed_price={listed_price}
              selling_price={selling_price}
              discount_value={discount_value}
              is_percentage={is_percentage}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
            />
            <div className={styles.packageFeatureOfferingContainer}>
              <AddPackageFeatureOffering
                newProductFeatureOffering={newProductFeatureOffering}
                newServiceFeatureOffering={newServiceFeatureOffering}
                handleChangeProductFeatureOffering={(
                  updatedNewProductFeatureOffering
                ) =>
                  setFieldValue(
                    "newProductFeatureOffering",
                    updatedNewProductFeatureOffering
                  )
                }
                handleChangeServiceFeatureOffering={(
                  updatedServiceFeatureOffering
                ) =>
                  setFieldValue(
                    "newServiceFeatureOffering",
                    updatedServiceFeatureOffering
                  )
                }
                handleAddFeatureOffering={() =>
                  handleAddFeatureOffering(
                    setFieldValue,
                    feature_offerings,
                    newProductFeatureOffering,
                    newServiceFeatureOffering
                  )
                }
                productFeatureOfferingErrors={
                  errors ? errors.newProductFeatureOffering : []
                }
                serviceFeatureOfferingErrors={
                  errors ? errors.newServiceFeatureOffering : []
                }
              />
              <CreatedPackageFeatureOfferings
                products={feature_offerings.products}
                services={feature_offerings.services}
                handleDeleteProductFeatureOffering={(featureOfferingPosition) =>
                  handleDeleteProductFeatureOffering(
                    featureOfferingPosition,
                    setFieldValue,
                    values
                  )
                }
                handleDeleteServiceFeatureOffering={(featureOfferingPosition) =>
                  handleDeleteServiceFeatureOffering(
                    featureOfferingPosition,
                    setFieldValue,
                    values
                  )
                }
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateNewPackageVersion;
